import Component from '@glimmer/component'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { service } from '@ember/service'
import { dropTask } from 'ember-concurrency'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type { IntlService } from 'ember-intl'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type DownloadFileService from 'teacher-ui-client/services/download-file'
import { usesExternalAuthentication } from 'teacher-ui-client/helpers/uses-external-authentication'
import PopoverDropdownStandard from '@blakeelearning/popovers/components/popover/dropdowns/standard'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { ActionMenu } from 'teacher-ui-client/components/base/action-menu'
import { ActionMenuItem } from 'teacher-ui-client/components/base/action-menu/item'
import { on } from '@ember/modifier'
import perform from 'ember-concurrency/helpers/perform'
import { t } from 'ember-intl'

interface Args {
  selectedIds: string[]
  disabled?: boolean
}

export type LanguageCode = 'eng' | 'chi' | 'fil' | 'fra' | 'ita' | 'khm' | 'kor' | 'pol' | 'rus' | 'spa' | 'vie'

export class DownloadParentLetters extends Component<Args> {
  @service downloadFile!: DownloadFileService

  @service intl!: IntlService

  @service subscriptionType!: SubscriptionTypeService

  @service flashQueue!: FlashQueueService

  get languageCodesForOptions() {
    return ['eng', 'chi', 'fil', 'fra', 'ita', 'khm', 'kor', 'pol', 'rus', 'spa', 'vie']
  }

  get parentLettersOptionsForEsl() {
    const options = this.languageCodesForOptions.map((languageCode) => ({
      text: this.intl.t(`languageNames.${languageCode}`),
      value: languageCode,
    }))
    return options.sort((a, b) => a.text.localeCompare(b.text))
  }

  parentLettersTask = dropTask(async (secondLanguageCode: LanguageCode) => {
    const params = {
      subscription_type: this.subscriptionType.current,
      student_ids: this.args.selectedIds,
      second_language_code: secondLanguageCode,
    }
    try {
      await this.downloadFile.downloadParentLettersForStudents(params)
      const successMessage = this.intl.t('manage.parentLettersDownloaded.success')
      this.flashQueue.addSuccess({ subtitle: successMessage })
    } catch {
      const message = this.intl.t('manage.parentLettersDownloaded.error')
      this.flashQueue.addFail({ subtitle: message })
    }
  }); /* prettier-ignore */ /* prettier-ignore-end */

  <template>
    {{#unless (usesExternalAuthentication)}}
      <PopoverDropdownStandard @disabled={{@disabled}} as |dropdown _status actions|>
        <UiButton data-test-letter-button disabled={{@disabled}} aria-haspopup="menu" {{dropdown.makeTrigger}}>
          {{t "manage.manageClasses.students.bulkActions.parentLettersForEsl"}}
          <PhosphorIcon @icon="phosphor-caret-down-bold" class="ml-2 h-3.5 w-3.5" />
        </UiButton>
        <dropdown.content>
          <ActionMenu>
            {{#each this.parentLettersOptionsForEsl as |option|}}
              <ActionMenuItem
                data-test-option={{option.value}}
                {{on "click" (perform this.parentLettersTask option.value)}}
                {{on "click" actions.close}}
              >
                {{option.text}}
              </ActionMenuItem>
            {{/each}}
          </ActionMenu>
        </dropdown.content>
      </PopoverDropdownStandard>
    {{/unless}}
  </template>
}

export default DownloadParentLetters
